import { useParams, useNavigate } from 'react-router-dom';
import { articles } from '@/mocks/seoArticle';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://superbuyspread.beauty';

function buildArticleJsonLd(article: { title: string; slug: string; excerpt: string; date: string }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    dateModified: article.date,
    author: {
      '@type': 'Organization',
      name: 'Superbuy Spreadsheet Hub',
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Superbuy Spreadsheet Hub',
      url: SITE_URL,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/article/${article.slug}`,
    },
  };
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const article = slug ? articles[slug] : undefined;
  const jsonLd = article ? buildArticleJsonLd(article) : null;

  if (!article) {
    return (
      <main>
        <Navbar />
        <div className="pt-32 pb-24 bg-background-50 min-h-screen flex items-center justify-center">
          <div className="text-center px-6">
            <h1 className="font-heading text-2xl font-bold text-primary-800 mb-3">Article Not Found</h1>
            <p className="text-foreground-500 mb-6">The article you are looking for does not exist or has been moved.</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 rounded-full bg-primary-800 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap"
            >
              Back to Homepage
            </button>
          </div>
        </div>
        <Footer />
      </main>
    );
  }

  const paragraphs = article.content.split('\n\n');

  return (
    <main>
      <Navbar />

      <article className="pt-24 pb-16 md:pt-32 md:pb-24 bg-background-50">
        <div className="px-6 md:px-10 lg:px-16">
          <div className="max-w-4xl mx-auto">
            <nav className="mb-8" aria-label="Breadcrumb">
              <ol className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
                <li>
                  <button
                    onClick={() => navigate('/')}
                    className="hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Home
                  </button>
                </li>
                <li>
                  <i className="ri-arrow-right-s-line text-xs"></i>
                </li>
                <li>
                  <span className="text-foreground-400 whitespace-nowrap">{article.category}</span>
                </li>
                <li>
                  <i className="ri-arrow-right-s-line text-xs"></i>
                </li>
                <li>
                  <span className="text-foreground-700 whitespace-nowrap truncate max-w-[200px]">{article.title.length > 40 ? article.title.substring(0, 40) + '...' : article.title}</span>
                </li>
              </ol>
            </nav>

            <header className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium whitespace-nowrap">
                  {article.category}
                </span>
                <span className="text-xs text-foreground-500">{article.date}</span>
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-primary-800 leading-tight mb-4">
                {article.title}
              </h1>
              <p className="text-foreground-600 text-base md:text-lg leading-relaxed">
                {article.excerpt}
              </p>
            </header>

            <div className="prose-custom">
              {paragraphs.map((paragraph, index) => {
                const trimmed = paragraph.trim();
                if (!trimmed) return null;

                if (trimmed.startsWith('## ')) {
                  const headingText = trimmed.replace('## ', '');
                  const headingId = headingText.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
                  return (
                    <h2 key={index} id={headingId} className="font-heading text-xl md:text-2xl font-semibold text-primary-800 mt-10 mb-4">
                      {headingText}
                    </h2>
                  );
                }

                return (
                  <p key={index} className="text-foreground-600 leading-relaxed mb-4 text-sm md:text-base">
                    {trimmed}
                  </p>
                );
              })}
            </div>

            <div className="mt-12 pt-8 border-t border-background-200/60">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-foreground-500 mb-2">
                    Was this guide helpful? Explore more resources:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => navigate('/')}
                      className="px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-sm hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      ← Back to Homepage
                    </button>
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="px-4 py-2 rounded-full bg-primary-800 text-background-50 text-sm hover:bg-primary-900 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Visit Superbuy Sheet
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />

      {jsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      )}
    </main>
  );
}